// This file is automatically generated. Do not edit it directly.
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const getEnvValue = (key: string): string | undefined => {
  if (typeof import.meta !== 'undefined' && import.meta?.env && key in import.meta.env) {
    return (import.meta.env as Record<string, string | undefined>)[key]?.trim();
  }
  if (typeof process !== 'undefined' && process?.env && key in process.env) {
    return process.env[key]?.trim();
  }
  return undefined;
};

// Trim whitespace and newlines from environment variables to prevent connection issues
const SUPABASE_URL = getEnvValue('VITE_SUPABASE_URL') || getEnvValue('SUPABASE_URL');
const SUPABASE_PUBLISHABLE_KEY = getEnvValue('VITE_SUPABASE_PUBLISHABLE_KEY') || getEnvValue('SUPABASE_ANON_KEY');

// Track missing environment variables for runtime validation
export const missingEnvVars: string[] = [];
if (!SUPABASE_URL) missingEnvVars.push('VITE_SUPABASE_URL');
if (!SUPABASE_PUBLISHABLE_KEY) missingEnvVars.push('VITE_SUPABASE_PUBLISHABLE_KEY');

// Helper to check if environment is properly configured
export const isSupabaseConfigured = missingEnvVars.length === 0;

const getBrowserStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;
  const storage = window.localStorage;
  if (storage && typeof storage.getItem === 'function') {
    return storage;
  }
  return undefined;
};

const browserStorage = getBrowserStorage();

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Create client only if environment is configured, otherwise create a mock client
// that will fail gracefully when used
let supabaseClient: SupabaseClient<Database>;

if (isSupabaseConfigured) {
  supabaseClient = createClient<Database>(SUPABASE_URL!, SUPABASE_PUBLISHABLE_KEY!, {
    auth: {
      storage: browserStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
    db: {
      schema: 'public',
    },
    global: {
      headers: {
        'apikey': SUPABASE_PUBLISHABLE_KEY!,
        'x-application-name': 'holescale',
      },
    },
  });
} else {
  // Create a placeholder that logs warnings instead of crashing
  // This allows the app to render and show a proper configuration error UI
  const placeholderUrl = 'https://placeholder.supabase.co';
  const placeholderKey = 'placeholder-key';

  // In production, log error but don't crash - let the app show a configuration error UI
  const isProd = typeof import.meta !== 'undefined' && Boolean(import.meta?.env?.PROD);
  if (isProd && typeof window !== 'undefined') {
    // Store error for UI to display instead of crashing
    (window as unknown as Record<string, unknown>).__SUPABASE_CONFIG_ERROR__ = {
      message: 'Missing required environment variables',
      missing: missingEnvVars,
    };
    // eslint-disable-next-line no-console
    console.error(
      `[Supabase] Critical Error: Missing required environment variables in production build.\n` +
      `Missing: ${missingEnvVars.join(', ')}\n` +
      `The application will show a configuration error.`
    );
  }

  supabaseClient = createClient<Database>(placeholderUrl, placeholderKey, {
    auth: {
      storage: browserStorage,
      persistSession: false,
      autoRefreshToken: false,
    },
  });

  // Log warning in development
  const isDev = typeof import.meta !== 'undefined' && Boolean(import.meta?.env?.DEV);
  if (typeof window !== 'undefined' && isDev) {
    // Use logger if available, otherwise console (for early initialization)
    if (typeof logger !== 'undefined') {
      logger.error(
        `[Supabase] Missing required environment variables: ${missingEnvVars.join(', ')}\n` +
        'Please ensure these are set in your .env file or environment configuration.\n' +
        'Copy .env.example to .env and fill in your Supabase credentials.'
      );
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `[Supabase] Missing required environment variables: ${missingEnvVars.join(', ')}\n` +
        'Please ensure these are set in your .env file or environment configuration.\n' +
        'Copy .env.example to .env and fill in your Supabase credentials.'
      );
    }
  }
}

export const supabase = supabaseClient;
